import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Button,
  TextControl,
  ToggleControl,
  RangeControl
} from "@wordpress/components"
import ServerSideRender from "@wordpress/server-side-render"
import { useState } from "@wordpress/element"

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: `hero-slides ${attributes.align ? `align${attributes.align}` : ''}`
  })

  const [isEditing, setIsEditing] = useState(false)
  const [editingSlide, setEditingSlide] = useState(0)

  const slides = attributes.slides || []
  const showArrows = attributes.showArrows !== false
  const autoplay = attributes.autoplay || false
  const autoplaySpeed = attributes.autoplaySpeed || 5000

  const addSlide = () => {
    const newSlides = [...slides, {
      title: "New Hero Title",
      date: "2025",
      description: "Add your description here.",
      linkText: "Get Involved",
      linkUrl: "",
      linkTarget: "_blank",
      image: {
        url: "",
        id: 0,
        alt: "Hero Image"
      }
    }]
    setAttributes({ slides: newSlides })
  }

  const removeSlide = (index) => {
    if (slides.length > 1) {
      const newSlides = slides.filter((_, i) => i !== index)
      setAttributes({ slides: newSlides })
      if (editingSlide >= newSlides.length) {
        setEditingSlide(newSlides.length - 1)
      }
    }
  }

  const updateSlide = (index, field, value) => {
    const newSlides = [...slides]
    newSlides[index] = { ...newSlides[index], [field]: value }
    setAttributes({ slides: newSlides })
  }

  const updateSlideImage = (index, media) => {
    const newSlides = [...slides]
    newSlides[index].image = {
      url: media.url,
      id: media.id,
      alt: media.alt || 'Hero Image'
    }
    setAttributes({ slides: newSlides })
  }

  // If we're editing, show the rich text editors instead of ServerSideRender
  if (isEditing) {
    const currentSlide = slides[editingSlide] || {}
    
    return (
      <>
        <InspectorControls>
          <PanelBody title={__('Hero Slides Settings', 'puroclean-tw')} initialOpen={true}>
            {/* Slide Management */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Slides:', 'puroclean-tw')} ({slides.length})
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <Button onClick={addSlide} variant="secondary" size="small">
                  {__('Add Slide', 'puroclean-tw')}
                </Button>
                {slides.length > 1 && (
                  <Button 
                    onClick={() => removeSlide(editingSlide)} 
                    variant="secondary" 
                    size="small"
                    isDestructive
                  >
                    {__('Remove Current Slide', 'puroclean-tw')}
                  </Button>
                )}
              </div>
              
              {/* Slide Navigation */}
              {slides.length > 1 && (
                <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
                  {slides.map((_, index) => (
                    <Button
                      key={index}
                      onClick={() => setEditingSlide(index)}
                      variant={index === editingSlide ? 'primary' : 'secondary'}
                      size="small"
                    >
                      {index + 1}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Current Slide Image */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Slide Image:', 'puroclean-tw')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={(media) => updateSlideImage(editingSlide, media)}
                  allowedTypes={['image']}
                  value={currentSlide.image?.id || 0}
                  render={({ open }) => (
                    <div>
                      {currentSlide.image?.url ? (
                        <div>
                          <img 
                            src={currentSlide.image.url} 
                            alt={currentSlide.image.alt}
                            style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                          />
                          <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                            {__('Change Image', 'puroclean-tw')}
                          </Button>
                          <Button 
                            onClick={() => updateSlideImage(editingSlide, { url: '', id: 0, alt: 'Hero Image' })} 
                            variant="link" 
                            isDestructive
                          >
                            {__('Remove Image', 'puroclean-tw')}
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={open} variant="secondary">
                          {__('Select Slide Image', 'puroclean-tw')}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </MediaUploadCheck>
            </div>

            {/* Link Target */}
            <ToggleControl
              label={__('Open link in new tab', 'puroclean-tw')}
              checked={currentSlide.linkTarget === '_blank'}
              onChange={(checked) => updateSlide(editingSlide, 'linkTarget', checked ? '_blank' : '_self')}
              style={{ marginBottom: '20px' }}
            />
          </PanelBody>

          <PanelBody title={__('Slider Settings', 'puroclean-tw')} initialOpen={false}>
            <ToggleControl
              label={__('Show Navigation Arrows', 'puroclean-tw')}
              checked={showArrows}
              onChange={(value) => setAttributes({ showArrows: value })}
            />
            <ToggleControl
              label={__('Autoplay', 'puroclean-tw')}
              checked={autoplay}
              onChange={(value) => setAttributes({ autoplay: value })}
            />
            {autoplay && (
              <RangeControl
                label={__('Autoplay Speed (ms)', 'puroclean-tw')}
                value={autoplaySpeed}
                onChange={(value) => setAttributes({ autoplaySpeed: value })}
                min={1000}
                max={10000}
                step={500}
              />
            )}
          </PanelBody>
        </InspectorControls>

        <div {...blockProps}>
          <div style={{ 
            border: '2px dashed #ddd', 
            padding: '40px 20px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>
              {__('Hero Slides - Edit Mode', 'puroclean-tw')} - {__('Slide', 'puroclean-tw')} {editingSlide + 1}
            </h3>
            
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Title:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Enter hero title...', 'puroclean-tw')}
                  value={currentSlide.title || ''}
                  onChange={(value) => updateSlide(editingSlide, 'title', value)}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '50px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Date:', 'puroclean-tw')}
                </label>
                <TextControl
                  value={currentSlide.date || ''}
                  onChange={(value) => updateSlide(editingSlide, 'date', value)}
                  placeholder={__('Enter date (e.g., 2025)', 'puroclean-tw')}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Description:', 'puroclean-tw')}
                </label>
                <RichText
                  tagName="div"
                  placeholder={__('Add your description here...', 'puroclean-tw')}
                  value={currentSlide.description || ''}
                  onChange={(value) => updateSlide(editingSlide, 'description', value)}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    minHeight: '120px',
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Link Text (Optional):', 'puroclean-tw')}
                </label>
                <TextControl
                  value={currentSlide.linkText || ''}
                  onChange={(value) => updateSlide(editingSlide, 'linkText', value)}
                  placeholder={__('Enter link text (e.g., Get Involved)', 'puroclean-tw')}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  {__('Link URL (Optional):', 'puroclean-tw')}
                </label>
                <TextControl
                  value={currentSlide.linkUrl || ''}
                  onChange={(value) => updateSlide(editingSlide, 'linkUrl', value)}
                  placeholder={__('Enter link URL', 'puroclean-tw')}
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '12px', 
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <Button 
                  variant="primary"
                  onClick={() => setIsEditing(false)}
                  style={{ fontSize: '16px', padding: '10px 20px' }}
                >
                  {__('Done Editing - View Preview', 'puroclean-tw')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show preview mode with ServerSideRender
  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Hero Slides Settings', 'puroclean-tw')} initialOpen={true}>
          <Button 
            variant="secondary"
            onClick={() => setIsEditing(true)}
            style={{ marginBottom: '15px', width: '100%' }}
          >
            {__('Edit Content', 'puroclean-tw')}
          </Button>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Slides:', 'puroclean-tw')} ({slides.length})
            </label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <Button onClick={addSlide} variant="secondary" size="small">
                {__('Add Slide', 'puroclean-tw')}
              </Button>
            </div>
          </div>
        </PanelBody>

        <PanelBody title={__('Slider Settings', 'puroclean-tw')} initialOpen={false}>
          <ToggleControl
            label={__('Show Navigation Arrows', 'puroclean-tw')}
            checked={showArrows}
            onChange={(value) => setAttributes({ showArrows: value })}
          />
          <ToggleControl
            label={__('Autoplay', 'puroclean-tw')}
            checked={autoplay}
            onChange={(value) => setAttributes({ autoplay: value })}
          />
          {autoplay && (
            <RangeControl
              label={__('Autoplay Speed (ms)', 'puroclean-tw')}
              value={autoplaySpeed}
              onChange={(value) => setAttributes({ autoplaySpeed: value })}
              min={1000}
              max={10000}
              step={500}
            />
          )}
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <ServerSideRender
          block="puroclean/hero-slides"
          attributes={attributes}
          urlQueryArgs={{ context: 'edit' }}
        />
      </div>
    </>
  )
}
